import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getFlag, setFlag, getWardrobe, equipCosmetic, save } from '../core/save.js';
import { addFound, hasFound } from '../core/collection.js';
import { questsForGiver, acceptQuest, turnIn, isReady, isCompleted, QUESTS, flagObjective } from '../core/quests.js';
import { RECIPES, learnRecipe } from '../core/recipes.js';
import { MoonKitchen } from '../minigames/moonKitchen.js';
import '../ui/space.css';

// Moon Castle — the throne room of the Moon King, reached from Moon Town. Cook a
// moon dish in the royal kitchen to earn the navigation chip, then (after
// installing it at the ship) report back to claim the astronaut suit + moon
// recipes that finish the Space Bread Program.

const STONE = 0x6E5E86, STONE_D = 0x54476A, ROYAL = 0x7A4A9E, GOLD = 0xF7C948, NIGHT = 0x140A24;
const KINGS_DISH = 'crater-souffle';
const ASTRO = [{ id: 'space-helmet', slot: 'hat' }, { id: 'oxygen-pack', slot: 'tie' }];
const MOON_RECIPES = ['moon-cheese-toast', 'crater-souffle', 'astro-burger', 'starlight-pudding'];

export function createMoonCastleScene(){ return () => new MoonCastleScene(); }

class MoonCastleScene {
  constructor(){
    this.panels = [];
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(NIGHT);
    this.scene.fog = new THREE.Fog(NIGHT, 26, 60);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 120);
    this.camDist = 14; this.camTarget = new THREE.Vector3(0, 1.8, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildHall();
    this._buildThrone();
    this._buildKitchen();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 9);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.king = this._makeKing(); this.scene.add(this.king.root);

    this.hotspots = [
      { pos: this.king.root.position.clone().add(new THREE.Vector3(0, 0, 2.4)), r: 2.6, label: '👑 address the Moon King', action: () => this._kingDialog() },
      { pos: new THREE.Vector3(-6, 0, 2), r: 2.4, label: '🍳 royal kitchen', action: () => this._cook() },
      { pos: new THREE.Vector3(0, 0, 11), r: 2.6, label: '← back to Moon Town', action: () => this.manager.goTo('moonTown', { from: 'moonCastle' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x9A88C0, 0.6));
    const key = new THREE.DirectionalLight(0xD8C8FF, 0.7); key.position.set(4, 12, 8);
    key.castShadow = true; key.shadow.mapSize.set(2048, 2048);
    const s = 20; key.shadow.camera.left = -s; key.shadow.camera.right = s; key.shadow.camera.top = s; key.shadow.camera.bottom = -s;
    this.scene.add(key);
    const glow = new THREE.PointLight(0xC0A0FF, 0.5, 22, 2); glow.position.set(0, 5, -6); this.scene.add(glow);
  }

  _buildHall(){
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 26), new THREE.MeshLambertMaterial({ color: STONE_D }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // royal carpet
    const rug = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 20), new THREE.MeshLambertMaterial({ color: ROYAL }));
    rug.rotation.x = -Math.PI/2; rug.position.set(0, 0.01, 0); this.scene.add(rug);
    // walls + columns
    box(16, 8, 0.6, STONE, 0, 4, -13, this.scene);
    box(0.6, 8, 26, STONE, -8, 4, 0, this.scene); box(0.6, 8, 26, STONE, 8, 4, 0, this.scene);
    for (let z = -10; z <= 8; z += 4.5){
      cyl(0.5, 0.6, 8, STONE, -6.5, 4, z, this.scene, 12); cyl(0.5, 0.6, 8, STONE, 6.5, 4, z, this.scene, 12);
    }
    // banners
    for (const x of [-6.5, 6.5]) box(1.2, 3, 0.1, ROYAL, x, 5, -12.6, this.scene);
    const sign = textSprite('👑 Hall of the Moon King', 24, '#EAE0FF', 5.0, 1.0); sign.position.set(0, 7.4, -12.5); this.scene.add(sign);
  }

  _buildThrone(){
    const g = new THREE.Group(); g.position.set(0, 0, -9); this.scene.add(g);
    box(3, 0.6, 3, STONE, 0, 0.3, 0, g);                     // dais
    box(1.6, 2.4, 0.4, ROYAL, 0, 1.8, -0.8, g);              // back
    box(1.8, 0.4, 1.4, ROYAL, 0, 0.9, 0, g);                 // seat
    for (const x of [-0.9, 0.9]){ cyl(0, 0.2, 0.6, GOLD, x, 3.1, -0.8, g, 8); }  // finials
    sphere(0.3, GOLD, 0, 3.3, -0.8, g, 12);
  }

  _buildKitchen(){
    const g = new THREE.Group(); g.position.set(-6, 0, 2); this.scene.add(g);
    box(2.2, 1.0, 1.0, 0x4A3E5A, 0, 0.5, 0, g);             // stove block
    cyl(0.3, 0.3, 0.2, 0x2A2036, -0.5, 1.05, 0, g, 12); cyl(0.3, 0.3, 0.2, 0x2A2036, 0.5, 1.05, 0, g, 12);
    sphere(0.18, 0xFF8A3C, -0.5, 1.2, 0, g, 8); sphere(0.18, 0xFF8A3C, 0.5, 1.2, 0, g, 8);   // flames
    box(0.8, 0.8, 0.1, 0xC0A8E0, 0, 1.6, -0.5, g);          // range hood
    const sign = textSprite('🍳 Royal Kitchen', 20, '#EAE0FF', 2.8, 0.8); sign.position.set(-6, 2.4, 2); this.scene.add(sign);
  }

  _makeKing(){
    const build = (g) => {
      sphere(0.9, 0xF2ECDA, 0, 1.1, 0, g, 20);                // big moon-face body
      for (let i = 0; i < 5; i++){ const a = Math.random()*6; sphere(0.1, 0xD8D0BE, Math.cos(a)*0.5, 1.1 + Math.sin(a)*0.4, 0.55, g, 8); } // craters
      sphere(.09, 0x2E2018, -0.22, 1.25, 0.72, g, 8); sphere(.09, 0x2E2018, 0.22, 1.25, 0.72, g, 8);
      box(0.34, 0.06, 0.05, 0x2E2018, 0, 1.02, 0.78, g);     // calm smile
      // crown
      cyl(0.5, 0.55, 0.3, GOLD, 0, 1.95, 0, g, 20);
      for (let i = 0; i < 6; i++){ const a = i/6*Math.PI*2; cyl(0, 0.11, 0.34, GOLD, Math.cos(a)*0.45, 2.2, Math.sin(a)*0.45, g, 8); }
      box(0.8, 1.0, 0.3, ROYAL, 0, 0.4, 0, g);               // robe
    };
    return new Townsperson({ name: 'Moon King', line: 'You have crossed the stars to stand before me. Welcome, little toast.', pos: new THREE.Vector3(0, 0, -8), facing: 0, wander: 0, speed: 0, bubbleY: 2.8, build });
  }

  // ---- interactions ----
  _cook(){
    if (getFlag('cookedForKing')){ HUD.say('The Moon King has already tasted your cooking. He was most pleased.'); return; }
    const recipe = RECIPES[KINGS_DISH] || { name: 'Crater Soufflé', icon: '🥮', ingredients: { moon_egg: 1, moon_cheese: 1, stardust: 1 } };
    const g = new MoonKitchen({ recipe,
      onWin: () => { setFlag('cookedForKing', true); flagObjective('moon-king', 'cookedForKing'); HUD.refreshQuests(); HUD.say('The Moon King loved your dish! Speak with him to receive the nav chip.'); this.bread.drawFace('yum'); },
      onClose: () => {} });
    g.mount();
  }

  _grantFinalReward(){
    // astronaut suit + moon recipes, granted once when the program is complete
    const w = getWardrobe();
    ASTRO.forEach(a => { if (!w.owned.includes(a.id)) w.owned.push(a.id); equipCosmetic(a.slot, a.id); });
    MOON_RECIPES.forEach(r => learnRecipe(r));

    // The Lunar Stabilizer MUST be handed over here, on turn-in. It used to be granted
    // only in _kingDialog(), which runs when you START talking — at which point
    // space-final is merely ready, not completed, so isCompleted() was false and the
    // player got nothing. Turning the quest in re-renders the panel without re-running
    // _kingDialog(), so the check never fired again and the king never handed it over —
    // soft-locking 'garys-stabilizer', which requires it. (_kingDialog still checks, as
    // a self-heal for saves already stuck past this point.)
    if (!hasFound('lunar-stabilizer')) addFound('lunar-stabilizer');

    save();
    HUD.say('🎉 The Starship Toaster flies again! The Moon King grants you the Astronaut Suit, a book of moon recipes, and a humming Lunar Stabilizer — Gary will know what to do with it.');
  }

  _kingDialog(){
    this.bread.stop(); this.bread.drawFace('grin');
    const to = new THREE.Vector3().subVectors(this.king.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z); this.bread.root.rotation.y = this.bread.facing;
    if (isCompleted('space-final') && !hasFound('lunar-stabilizer')){
      addFound('lunar-stabilizer');
      HUD.say('The Moon King hands you a humming Lunar Stabilizer. Gary will know what to do with it.');
    }
    this._renderKing();
  }

  _renderKing(){
    this._clearPanels();
    const { available, active } = questsForGiver('moonking');
    const rows = [];
    active.forEach(id => { const q = QUESTS[id]; rows.push(isReady(id)
      ? `<div class="q-drow ready"><div><b>${q.title}</b><span>done — claim reward</span></div><button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`
      : `<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><span class="q-dpend">in progress</span></div>`); });
    available.forEach(id => { const q = QUESTS[id]; rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc} · 🍞 ${q.reward?.crumbs||0}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`); });
    const body = rows.length ? rows.join('') : `<p class="q-dnone">"The night smiles upon you, traveler. Safe voyages among the stars."</p>`;
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>The Moon King</h2>
        <div class="cafe-sub">keeper of the night</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); HUD.refreshQuests(); this._renderKing(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => {
      const id = b.dataset.id;
      if (turnIn(id)){
        HUD.refreshCrumbs(); HUD.refreshQuests(); this.bread.drawFace('yum');
        if (id === 'space-final') this._grantFinalReward();
      }
      this._renderKing();
    });
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _panel(html){ const el = document.createElement('div'); el.className = 'cafe-panel'; el.innerHTML = html; document.body.appendChild(el); this.panels.push(el); return el; }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager; this.input = manager.input;
    this.input.useCamera(this.camera); this.input.polarRange = [0.3, 0.7];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the moon castle'); HUD.refreshCrumbs(); HUD.refreshQuests();
    HUD.say('The Moon King\'s hall. Cook a moon dish in the royal kitchen, then address the King to earn the navigation chip.');
    HUD.setHint('click to walk · cook in the royal kitchen · address the Moon King');
    HUD.hideHintSoon(8000);
  }
  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, [this.king], this.bread)) return;
    const dir = ray.ray.direction; if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y; if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(Math.max(-7, Math.min(7, p.x)), 0, Math.max(-6, Math.min(11, p.z)));
    HUD.hideHint(); this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this.king.update(dt, this.bread.root.position);
    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.8, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){ this.camera.aspect = window.innerWidth / window.innerHeight; this.camera.updateProjectionMatrix(); }
}
